import React from 'react';
import {
  EVENT_KIND_FILTERS,
  EVENT_STATUS_FILTERS,
  EVENT_SORT_OPTIONS,
} from '../utils/eventListFilter';

function ChipGroup({ label, options, value, onChange }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0" role="group" aria-label={label}>
      <span className="sr-only">{label}</span>
      <div className="flex flex-wrap items-center gap-1 rounded-lg bg-gray-100/80 dark:bg-gray-800/80 p-0.5">
        {options.map((opt) => {
          const selected = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              aria-pressed={selected}
              className={`px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                selected
                  ? 'bg-white dark:bg-gray-900 text-gray-950 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact filter / sort bar for landing event lists (featured, matches, polls).
 */
export default function EventListToolbar({
  filters,
  onKindChange,
  onStatusChange,
  onCupChange,
  onSortChange,
  onQueryChange,
  onReset,
  isActive = false,
  showKind = false,
  showCup = true,
  cups = [],
  resultCount = 0,
  totalCount = 0,
  searchPlaceholder = 'Search games',
  statusOptions = EVENT_STATUS_FILTERS,
}) {
  return (
    <div className="mb-4 space-y-2.5">
      <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {showKind ? (
            <ChipGroup
              label="Type"
              options={EVENT_KIND_FILTERS}
              value={filters.kind}
              onChange={onKindChange}
            />
          ) : null}
          <ChipGroup
            label="Status"
            options={statusOptions}
            value={filters.status}
            onChange={onStatusChange}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {showCup && cups.length > 1 ? (
            <label className="relative inline-flex items-center">
              <span className="sr-only">Tournament</span>
              <select
                value={filters.cup}
                onChange={(e) => onCupChange(e.target.value)}
                className="appearance-none pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-semibold text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400"
              >
                <option value="all">All tournaments</option>
                {cups.map((cup) => (
                  <option key={cup.slug} value={cup.slug}>
                    {cup.name}
                  </option>
                ))}
              </select>
              <svg
                className="pointer-events-none absolute right-2.5 w-3.5 h-3.5 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </label>
          ) : null}

          <label className="relative inline-flex items-center">
            <span className="sr-only">Sort</span>
            <select
              value={filters.sort}
              onChange={(e) => onSortChange(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-semibold text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400"
            >
              {EVENT_SORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  Sort: {opt.label}
                </option>
              ))}
            </select>
            <svg
              className="pointer-events-none absolute right-2.5 w-3.5 h-3.5 text-gray-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </label>

          <label className="relative flex-1 min-w-[10rem] sm:min-w-[12rem]">
            <span className="sr-only">Search</span>
            <svg
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
            </svg>
            <input
              type="search"
              value={filters.query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400"
            />
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 text-[11px] text-gray-500 dark:text-gray-400">
        <p className="tabular-nums">
          {resultCount === totalCount
            ? `${resultCount} ${resultCount === 1 ? 'game' : 'games'}`
            : `Showing ${resultCount} of ${totalCount}`}
        </p>
        {isActive ? (
          <button
            type="button"
            onClick={onReset}
            className="font-semibold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
          >
            Reset filters
          </button>
        ) : null}
      </div>
    </div>
  );
}
